<script setup>
import { onMounted, computed, reactive } from "vue";
import { usePayrollConfigStore } from "@/stores/payrollConfigStore";
import layoutHeader from "@/views/layouts/layout-header.vue";
import layoutSidebar from "@/views/layouts/layout-sidebar.vue";
//import breadcrumb from "@/components/common/breadcrumb.vue";

const payrollConfigStore = usePayrollConfigStore();

const productionReadyCountries = new Set(["KE"]);

const title = "Payroll Settings";
const text = "Payroll Company Settings";
const text1 = "Settings";

// Form state
const form = reactive({
  country: "KE",

  nssf_lower_limit: 8000,
  nssf_upper_limit: 72000,
  nssf_rate: 0.06,
  nssf_employee_cap: 4320,
  nssf_employer_match: true,

  shif_rate: 0.0275,
  shif_min: 300,

  ahl_rate: 0.015,

  personal_relief: 2400,
  insurance_relief_rate: 0.15,
  insurance_relief_cap: 5000,

  paye_bands: [
    { up_to: 24000, rate: 0.1 },
    { up_to: 32333, rate: 0.25 },
    { up_to: 500000, rate: 0.3 },
    { up_to: 800000, rate: 0.325 },
  ],
  paye_top_rate: 0.35,
});

const loading = computed(() => payrollConfigStore.loading);
const isDraftPresetCountry = computed(
  () => !productionReadyCountries.has(form.country),
);

// Country list
const countryOptions = [
  { code: "KE", name: "Kenya" },
  { code: "UG", name: "Uganda" },
  { code: "TZ", name: "Tanzania" },
  { code: "RW", name: "Rwanda" },
  { code: "BI", name: "Burundi" },
  { code: "SS", name: "South Sudan" },
  { code: "ET", name: "Ethiopia" },
];

// ---- Preset definitions (numbers for non-Kenya are placeholders; update as needed) ----
const presets = {
  KE: {
    country: "KE",
    nssf_lower_limit: 8000,
    nssf_upper_limit: 72000,
    nssf_rate: 0.06,
    nssf_employee_cap: 4320,
    nssf_employer_match: true,
    shif_rate: 0.0275,
    shif_min: 300,
    ahl_rate: 0.015,
    personal_relief: 2400,
    insurance_relief_rate: 0.15,
    insurance_relief_cap: 5000,
    paye_bands: [
      { up_to: 24000, rate: 0.1 },
      { up_to: 32333, rate: 0.25 },
      { up_to: 500000, rate: 0.3 },
      { up_to: 800000, rate: 0.325 },
    ],
    paye_top_rate: 0.35,
  },

  // TODO: Replace with official country-specific figures before going live
  UG: {
    country: "UG",
    nssf_lower_limit: 0,
    nssf_upper_limit: 0,
    nssf_rate: 0.05,
    nssf_employee_cap: 0,
    nssf_employer_match: false,
    shif_rate: 0,
    shif_min: 0,
    ahl_rate: 0,
    personal_relief: 0,
    insurance_relief_rate: 0,
    insurance_relief_cap: 0,
    paye_bands: [
      { up_to: 235000, rate: 0.1 },
      { up_to: 335000, rate: 0.2 },
      { up_to: 410000, rate: 0.3 },
      { up_to: 999999999, rate: 0.4 },
    ],
    paye_top_rate: 0.4,
  },

  TZ: {
    country: "TZ",
    nssf_lower_limit: 0,
    nssf_upper_limit: 0,
    nssf_rate: 0.1,
    nssf_employee_cap: 0,
    nssf_employer_match: false,
    shif_rate: 0,
    shif_min: 0,
    ahl_rate: 0,
    personal_relief: 0,
    insurance_relief_rate: 0,
    insurance_relief_cap: 0,
    paye_bands: [
      { up_to: 270000, rate: 0.0 },
      { up_to: 520000, rate: 0.09 },
      { up_to: 760000, rate: 0.2 },
      { up_to: 1000000, rate: 0.25 },
    ],
    paye_top_rate: 0.3,
  },

  RW: {
    country: "RW",
    nssf_lower_limit: 0,
    nssf_upper_limit: 0,
    nssf_rate: 0.05,
    nssf_employee_cap: 0,
    nssf_employer_match: false,
    shif_rate: 0,
    shif_min: 0,
    ahl_rate: 0,
    personal_relief: 0,
    insurance_relief_rate: 0,
    insurance_relief_cap: 0,
    paye_bands: [
      { up_to: 30000, rate: 0.0 },
      { up_to: 100000, rate: 0.2 },
      { up_to: 999999999, rate: 0.3 },
    ],
    paye_top_rate: 0.3,
  },
};

// Apply preset, but user can still tweak values before saving
function applyPreset(code) {
  const preset = presets[code];
  if (!preset) return;

  form.country = preset.country;

  form.nssf_lower_limit = preset.nssf_lower_limit;
  form.nssf_upper_limit = preset.nssf_upper_limit;
  form.nssf_rate = preset.nssf_rate;
  form.nssf_employee_cap = preset.nssf_employee_cap;
  form.nssf_employer_match = preset.nssf_employer_match;

  form.shif_rate = preset.shif_rate;
  form.shif_min = preset.shif_min;

  form.ahl_rate = preset.ahl_rate;

  form.personal_relief = preset.personal_relief;
  form.insurance_relief_rate = preset.insurance_relief_rate;
  form.insurance_relief_cap = preset.insurance_relief_cap;

  form.paye_bands = preset.paye_bands.map((b) => ({
    up_to: b.up_to,
    rate: b.rate,
  }));
  form.paye_top_rate = preset.paye_top_rate;
}

// Load config on mount
onMounted(async () => {
  await payrollConfigStore.fetchConfig();
  const cfg = payrollConfigStore.config || {};

  form.country = cfg.country || "KE";

  const nssf = cfg.nssf || {};
  const shif = cfg.shif || {};
  const ahl = cfg.ahl || {};
  const paye = cfg.paye || {};

  form.nssf_lower_limit = nssf.lower_limit ?? 8000;
  form.nssf_upper_limit = nssf.upper_limit ?? 72000;
  form.nssf_rate = nssf.rate ?? 0.06;
  form.nssf_employee_cap = nssf.employee_cap ?? 4320;
  form.nssf_employer_match =
    typeof nssf.employer_match === "boolean" ? nssf.employer_match : true;

  form.shif_rate = shif.rate ?? 0.0275;
  form.shif_min = shif.min ?? 300;

  form.ahl_rate = ahl.rate ?? 0.015;

  form.personal_relief = paye.personal_relief ?? 2400;
  form.insurance_relief_rate = paye.insurance_relief_rate ?? 0.15;
  form.insurance_relief_cap = paye.insurance_relief_cap ?? 5000;

  const bands =
    Array.isArray(paye.bands) && paye.bands.length
      ? paye.bands
      : [
          { up_to: 24000, rate: 0.1 },
          { up_to: 32333, rate: 0.25 },
          { up_to: 500000, rate: 0.3 },
          { up_to: 800000, rate: 0.325 },
        ];

  form.paye_bands = [
    { up_to: bands[0]?.up_to ?? 24000, rate: bands[0]?.rate ?? 0.1 },
    { up_to: bands[1]?.up_to ?? 32333, rate: bands[1]?.rate ?? 0.25 },
    { up_to: bands[2]?.up_to ?? 500000, rate: bands[2]?.rate ?? 0.3 },
    { up_to: bands[3]?.up_to ?? 800000, rate: bands[3]?.rate ?? 0.325 },
  ];

  form.paye_top_rate = paye.top_rate ?? 0.35;
});

async function handleSave() {
  if (
    isDraftPresetCountry.value &&
    !window.confirm(
      `${form.country} presets are draft placeholders. Verify statutory figures with a tax adviser before tenants use them. Save anyway?`,
    )
  ) {
    return;
  }

  const payload = {
    country: form.country,

    nssf: {
      lower_limit: Number(form.nssf_lower_limit),
      upper_limit: Number(form.nssf_upper_limit),
      rate: Number(form.nssf_rate),
      employee_cap: Number(form.nssf_employee_cap),
      employer_match: !!form.nssf_employer_match,
    },

    shif: {
      rate: Number(form.shif_rate),
      min: Number(form.shif_min),
    },

    ahl: {
      rate: Number(form.ahl_rate),
    },

    paye: {
      personal_relief: Number(form.personal_relief),
      insurance_relief_rate: Number(form.insurance_relief_rate),
      insurance_relief_cap: Number(form.insurance_relief_cap),
      bands: form.paye_bands
        .filter((b) => b.up_to && b.rate)
        .map((b) => ({
          up_to: Number(b.up_to),
          rate: Number(b.rate),
        })),
      top_rate: Number(form.paye_top_rate),
    },
  };

  await payrollConfigStore.saveConfig(payload);
}
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
      </div>

      <div class="card">
        <div class="card-header">
          <h5 class="mb-1">Payroll Configuration</h5>
          <small class="text-muted d-block">
            Configure statutory rates and thresholds. Defaults follow Kenyan
            rules but can be adjusted per company for any East African country.
          </small>
        </div>
        <div class="card-body">
          <div
            class="alert mb-3"
            :class="isDraftPresetCountry ? 'alert-warning' : 'alert-success'"
          >
            <template v-if="form.country === 'KE'">
              Kenya (KE) presets match the current production statutory model
              (NSSF tiers, SHA, AHL, PAYE bands). Review after each Finance Act.
            </template>
            <template v-else>
              <strong>Draft preset:</strong> {{ form.country }} figures are
              placeholders for SuperAdmin setup only. Do not treat them as
              official tax guidance until validated for production.
            </template>
          </div>

          <form @submit.prevent="handleSave">
            <!-- Country & Presets -->
            <div class="row g-4 mb-3">
              <div class="col-md-4">
                <label class="form-label">Country / Tax Regime</label>
                <select v-model="form.country" class="form-select">
                  <option
                    v-for="c in countryOptions"
                    :key="c.code"
                    :value="c.code"
                  >
                    {{ c.name }} ({{ c.code }})
                  </option>
                </select>
                <div class="form-text">
                  This is informational. Actual computations depend on the
                  figures you set below.
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label">Apply Preset</label>
                <div class="btn-group d-block">
                  <button
                    type="button"
                    class="btn btn-outline-secondary dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    Choose Country Preset
                  </button>
                  <ul class="dropdown-menu">
                    <li v-for="c in countryOptions" :key="c.code">
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item"
                        @click.prevent="applyPreset(c.code)"
                      >
                        {{ c.name }} Preset
                      </a>
                    </li>
                  </ul>
                </div>
                <div class="form-text">
                  Use a preset as a starting point, then adjust if needed.
                </div>
              </div>
            </div>

            <!-- NSSF -->
            <div class="row g-4">
              <div class="col-12">
                <h6 class="border-bottom pb-2 mb-3">NSSF Settings</h6>
              </div>
              <div class="col-md-3">
                <label class="form-label">Lower Limit (Tier I)</label>
                <input
                  v-model.number="form.nssf_lower_limit"
                  type="number"
                  min="0"
                  class="form-control"
                  required
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">Upper Limit (Tier II)</label>
                <input
                  v-model.number="form.nssf_upper_limit"
                  type="number"
                  min="0"
                  class="form-control"
                  required
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">Contribution Rate</label>
                <div class="input-group">
                  <input
                    v-model.number="form.nssf_rate"
                    type="number"
                    step="0.0001"
                    min="0"
                    class="form-control"
                    required
                  />
                  <span class="input-group-text">e.g. 0.06 = 6%</span>
                </div>
              </div>
              <div class="col-md-3">
                <label class="form-label">Employee Cap</label>
                <input
                  v-model.number="form.nssf_employee_cap"
                  type="number"
                  min="0"
                  class="form-control"
                  required
                />
              </div>
              <div class="col-md-3 mt-2">
                <div class="form-check form-switch mt-4">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="nssf_employer_match"
                    v-model="form.nssf_employer_match"
                  />
                  <label class="form-check-label" for="nssf_employer_match">
                    Employer matches NSSF
                  </label>
                </div>
              </div>
            </div>

            <!-- SHIF -->
            <div class="row g-4 mt-4">
              <div class="col-12">
                <h6 class="border-bottom pb-2 mb-3">SHIF / NHIF Settings</h6>
              </div>
              <div class="col-md-3">
                <label class="form-label">Rate</label>
                <div class="input-group">
                  <input
                    v-model.number="form.shif_rate"
                    type="number"
                    step="0.0001"
                    min="0"
                    class="form-control"
                    required
                  />
                  <span class="input-group-text">e.g. 0.0275 = 2.75%</span>
                </div>
              </div>
              <div class="col-md-3">
                <label class="form-label">Minimum Contribution</label>
                <input
                  v-model.number="form.shif_min"
                  type="number"
                  min="0"
                  class="form-control"
                  required
                />
              </div>
            </div>

            <!-- AHL -->
            <div class="row g-4 mt-4">
              <div class="col-12">
                <h6 class="border-bottom pb-2 mb-3">
                  Affordable Housing Levy (Employee)
                </h6>
              </div>
              <div class="col-md-3">
                <label class="form-label">Rate</label>
                <div class="input-group">
                  <input
                    v-model.number="form.ahl_rate"
                    type="number"
                    step="0.0001"
                    min="0"
                    class="form-control"
                    required
                  />
                  <span class="input-group-text">e.g. 0.015 = 1.5%</span>
                </div>
              </div>
            </div>

            <!-- PAYE Reliefs -->
            <div class="row g-4 mt-4">
              <div class="col-12">
                <h6 class="border-bottom pb-2 mb-3">PAYE Reliefs</h6>
              </div>
              <div class="col-md-3">
                <label class="form-label">Personal Relief</label>
                <input
                  v-model.number="form.personal_relief"
                  type="number"
                  min="0"
                  class="form-control"
                  required
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">Insurance Relief Rate</label>
                <div class="input-group">
                  <input
                    v-model.number="form.insurance_relief_rate"
                    type="number"
                    step="0.0001"
                    min="0"
                    class="form-control"
                    required
                  />
                  <span class="input-group-text">e.g. 0.15 = 15%</span>
                </div>
              </div>
              <div class="col-md-3">
                <label class="form-label">Insurance Relief Cap</label>
                <input
                  v-model.number="form.insurance_relief_cap"
                  type="number"
                  min="0"
                  class="form-control"
                  required
                />
              </div>
            </div>

            <!-- PAYE Bands -->
            <div class="row g-4 mt-4">
              <div class="col-12">
                <h6 class="border-bottom pb-2 mb-3">PAYE Tax Bands</h6>
                <div class="form-text">
                  Income in each band is taxed at the specified rate. Any income
                  above the last band uses the Top Rate.
                </div>
              </div>

              <div class="col-12">
                <div class="table-responsive">
                  <table class="table table-bordered align-middle">
                    <thead class="table-light">
                      <tr>
                        <th style="width: 35%">Band</th>
                        <th style="width: 35%">Upper Limit</th>
                        <th style="width: 30%">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(band, idx) in form.paye_bands" :key="idx">
                        <td>Band {{ idx + 1 }}</td>
                        <td>
                          <input
                            v-model.number="band.up_to"
                            type="number"
                            min="0"
                            class="form-control"
                            required
                          />
                        </td>
                        <td>
                          <div class="input-group">
                            <input
                              v-model.number="band.rate"
                              type="number"
                              step="0.0001"
                              min="0"
                              class="form-control"
                              required
                            />
                            <span class="input-group-text"
                              >e.g. 0.10 = 10%</span
                            >
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td>Top Rate</td>
                        <td>Above last band</td>
                        <td>
                          <div class="input-group">
                            <input
                              v-model.number="form.paye_top_rate"
                              type="number"
                              step="0.0001"
                              min="0"
                              class="form-control"
                              required
                            />
                            <span class="input-group-text"
                              >e.g. 0.35 = 35%</span
                            >
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="mt-4 d-flex justify-content-end">
              <button type="submit" class="btn btn-primary" :disabled="loading">
                <span
                  v-if="loading"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                Save Configuration
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
</template>


